import { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../lib/auth";
import {
  LayoutDashboard, ShoppingCart, Package, Grid3X3, Ticket, Star,
  DollarSign, CreditCard, Settings, User, Clock, UtensilsCrossed,
  Sparkles, Wallet, HandCoins, ChevronDown, X, PanelLeftClose,
  PanelLeftOpen, ChevronRight, Truck,
  Upload,
} from "lucide-react";
import { clsx } from "clsx";

// Build navigation sections. Dine-in is conditionally included at render time.
const baseNavigation = [
  { label: "MAIN", items: [
    { name: "Dashboard", icon: LayoutDashboard, path: "/" },
    { name: "Orders", icon: ShoppingCart, path: "/orders", badgeKey: "orders" },
  ]},
  { label: "ORDERS", items: [
    { name: "Placed", icon: Clock, path: "/orders/placed", badgeKey: "placed" },
    { name: "Accepted", icon: ShoppingCart, path: "/orders/accepted" },
  ]},
  { label: "CATALOG", items: [
    { name: "Products", icon: Package, path: "/products" },
  ]},
  { label: "REVIEWS", items: [
    { name: "Customer Reviews", icon: Star, path: "/reviews" },
  ]},
  { label: "PAYMENTS", items: [
    { name: "Earnings", icon: DollarSign, path: "/payments/earnings" },
    { name: "Payouts", icon: Wallet, path: "/payments/payouts" },
  ]},
  // Dine-In + Delivery are injected dynamically
  { label: "SETTINGS", items: [
    { name: "Store Settings", icon: Settings, path: "/settings/store" },
    // DELIVERY-MOVE-1 — Delivery Settings sidebar entry removed; managed centrally in admin panel.
    // Route still exists and shows a redirect banner for old bookmarks.
    { name: "Profile", icon: User, path: "/settings/profile" },
  ]},
];

const dineInSection = {
  label: "DINE-IN",
  items: [
    { name: "Bookings", icon: UtensilsCrossed, path: "/dinein" },
  ],
};
const groceryCatalogueSection = {
  label: "CATALOGUE",
  items: [
    { name: "Catalogue Upload", icon: Upload, path: "/catalogue" },
    { name: "New Item Requests", icon: Package, path: "/catalogue/new-items" },
  ],
};
// Store-own delivery — only when admin set the store to self-delivery.
const deliverySection = {
  label: "DELIVERY",
  items: [
    { name: "Delivery Team", icon: Truck, path: "/delivery-team" },
  ],
};


export default function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse }) {
  const location = useLocation();
  // GHOST-FIX-STORE-WEB #25: pending-orders count comes from the shared
  // AuthContext listener — Sidebar + Topbar both read the same value.
  const { vendorName, vendor, allVendors, switchVendor, pendingOrderCount: authPending } = useAuth();
  const pendingOrderCount = authPending || 0;
  const placedOrderCount = authPending || 0;
  const [collapsedSections, setCollapsedSections] = useState({});
  const [storeSwitcherOpen, setStoreSwitcherOpen] = useState(false);

  const toggleSection = (label) =>
    setCollapsedSections((prev) => ({ ...prev, [label]: !prev[label] }));

  // Build navigation with conditional dine-in
  const navigation = useMemo(() => {
    const showDineIn = vendor?.enabledDineIn || vendor?.dineIn;
    const isGrocery = vendor?.section_id === "6319dc53314ee";
    const isSelfDelivery = vendor?.deliveryMode === "self";
    if (!showDineIn && !isGrocery && !isSelfDelivery) return baseNavigation;
    // Insert conditional sections before SETTINGS
    const nav = [...baseNavigation];
    const insertBeforeSettings = (section) => {
      const idx = nav.findIndex((s) => s.label === "SETTINGS");
      if (idx !== -1) nav.splice(idx, 0, section);
      else nav.push(section);
    };
    if (isGrocery) insertBeforeSettings(groceryCatalogueSection);
    if (showDineIn) insertBeforeSettings(dineInSection);
    if (isSelfDelivery) insertBeforeSettings(deliverySection);
    return nav;
  }, [vendor?.enabledDineIn, vendor?.dineIn, vendor?.section_id, vendor?.deliveryMode]);

  const isPathActive = (itemPath) => {
    if (itemPath === "/") return location.pathname === "/";
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + "/");
  };

  const getBadge = (badgeKey) => {
    if (badgeKey === "orders" && pendingOrderCount > 0) return pendingOrderCount;
    if (badgeKey === "placed" && placedOrderCount > 0) return placedOrderCount;
    return null;
  };

  // GHOST-FIX-STORE-WEB #44: Open/Closed driven purely by the manual toggle
  // (reststatus) AND today's schedule. We previously also short-circuited
  // on storeStatus, which forced "Closed" for vendors that never set that
  // legacy field. Now the badge mirrors what the schedule actually says.
  // Truthy when: reststatus === true AND today.isOpen === true AND now in any [from,to].
  const isStoreOpen = (() => {
    const v = vendor;
    if (!v) return false;
    if (v.reststatus !== true) return false;
    if (v.alwaysAcceptOrders === true) return true;
    const wh = v.workingHours;
    if (!Array.isArray(wh) || wh.length === 0) return false;
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const today = wh.find((h) => h.day === dayNames[new Date().getDay()]);
    if (!today) return false;
    // Per-day enabled flag is persisted as `isOpen` by StoreSettings.
    const dayEnabled = today.isOpen === true || today.enabled === true;
    if (!dayEnabled) return false;
    if (!Array.isArray(today.timeslot) || today.timeslot.length === 0) return false;
    const toMin = (str) => {
      if (!str || typeof str !== "string") return null;
      const t = str.trim();
      let m = t.match(/^([0-9]{1,2}):([0-9]{2})\s*(AM|PM|am|pm)$/);
      if (m) { let h = Number(m[1]) % 12; if (m[3].toUpperCase() === "PM") h += 12; return h * 60 + Number(m[2]); }
      m = t.match(/^([0-9]{1,2}):([0-9]{2})/);
      return m ? Math.min(23, Number(m[1])) * 60 + Number(m[2]) : null;
    };
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    for (const slot of today.timeslot) {
      const a = toMin(slot.from); let b = toMin(slot.to);
      if (a == null || b == null) continue;
      if (b <= a) b += 1440;
      if (cur >= a && cur <= b) return true;
    }
    return false;
  })();
  const vendorPhoto = vendor?.photo || vendor?.profilePictureURL || null;
  const displayName = vendorName || vendor?.title || vendor?.name || "Store";
  const sidebarWidth = collapsed ? "w-[72px]" : "w-[260px]";

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          "fixed top-0 left-0 z-50 h-full bg-sidebar flex flex-col transition-all duration-300 ease-in-out",
          sidebarWidth,
          "lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-white/10 shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-1">
              <span className="text-2xl font-bold tracking-tight">
                <span className="text-amigo-orange">ami</span>
                <span className="text-amigo-teal">Go</span>
              </span>
              <span className="text-[10px] font-medium text-white/40 uppercase tracking-widest ml-1.5 mt-1">
                Store
              </span>
            </div>
          )}
          {collapsed && (
            <div className="mx-auto">
              <span className="text-lg font-bold">
                <span className="text-amigo-orange">a</span>
                <span className="text-amigo-teal">G</span>
              </span>
            </div>
          )}
          <button
            onClick={onClose}
            className="lg:hidden p-1 text-white/50 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Store Switcher ── */}
        {!collapsed && allVendors.length > 1 && (
          <div className="px-3 py-3 border-b border-white/10">
            <div className="relative">
              <button
                onClick={() => setStoreSwitcherOpen((p) => !p)}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-sm transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {vendorPhoto ? (
                    <img
                      src={vendorPhoto}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-amigo-orange/20 flex items-center justify-center text-amigo-orange text-[10px] font-bold shrink-0">
                      {displayName[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="truncate">{displayName}</span>
                </div>
                <ChevronDown
                  size={14}
                  className={clsx(
                    "shrink-0 text-white/40 transition-transform",
                    storeSwitcherOpen && "rotate-180"
                  )}
                />
              </button>

              {storeSwitcherOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1f2e] border border-white/10 rounded-lg overflow-hidden shadow-xl z-50">
                  {allVendors.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => {
                        switchVendor(v.id);
                        setStoreSwitcherOpen(false);
                      }}
                      className={clsx(
                        "flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left transition-colors",
                        v.id === vendor?.id
                          ? "bg-amigo-orange/10 text-amigo-orange"
                          : "text-white/60 hover:bg-white/5 hover:text-white"
                      )}
                    >
                      {(v.photo || v.profilePictureURL) ? (
                        <img
                          src={v.photo || v.profilePictureURL}
                          alt=""
                          className="w-5 h-5 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-bold shrink-0">
                          {(v.title || v.name || "S")[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="truncate">{v.title || v.name}</span>
                      {v.id === vendor?.id && (
                        <span className="ml-auto text-[10px] text-amigo-orange/70">Active</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Navigation ── */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-2 space-y-1">
          {navigation.map((section) => (
            <div key={section.label} className="mb-2">
              {!collapsed && (
                <button
                  onClick={() => toggleSection(section.label)}
                  className="flex items-center justify-between w-full px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/40 hover:text-white/60 transition-colors"
                >
                  <span>{section.label}</span>
                  {collapsedSections[section.label] ? (
                    <ChevronRight size={12} />
                  ) : (
                    <ChevronDown size={12} />
                  )}
                </button>
              )}
              {(collapsed || !collapsedSections[section.label]) && (
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isPathActive(item.path);
                    const badge = item.badgeKey ? getBadge(item.badgeKey) : null;

                    return (
                      <div key={item.path} className="relative group">
                        <NavLink
                          to={item.path}
                          onClick={() => {
                            if (window.innerWidth < 1024) onClose();
                          }}
                          className={clsx(
                            "flex items-center gap-3 rounded-lg text-[13px] font-medium transition-all duration-200",
                            collapsed ? "px-0 py-2.5 justify-center" : "px-3 py-2.5",
                            isActive
                              ? "bg-amigo-orange/10 text-amigo-orange border-l-[3px] border-amigo-orange"
                              : "text-white/60 hover:bg-sidebar-hover hover:text-white border-l-[3px] border-transparent"
                          )}
                        >
                          <Icon
                            size={18}
                            className={clsx(
                              "shrink-0 transition-colors",
                              isActive
                                ? "text-amigo-orange"
                                : "text-white/40 group-hover:text-white/70"
                            )}
                          />
                          {!collapsed && (
                            <>
                              <span className="flex-1">{item.name}</span>
                              {badge && (
                                <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-amigo-orange text-white text-[10px] font-bold rounded-full">
                                  {badge > 99 ? "99+" : badge}
                                </span>
                              )}
                            </>
                          )}
                          {collapsed && badge && (
                            <span className="absolute top-1 right-1 w-2 h-2 bg-amigo-orange rounded-full" />
                          )}
                        </NavLink>

                        {/* Collapsed tooltip */}
                        {collapsed && (
                          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                            {item.name}
                            {badge && (
                              <span className="ml-1.5 px-1.5 py-0.5 bg-amigo-orange rounded-full text-[10px] font-bold">
                                {badge}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* ── Vendor Profile Footer ── */}
        {!collapsed && (
          <div className="px-4 py-3 border-t border-white/10 shrink-0">
            <div className="flex items-center gap-3">
              {vendorPhoto ? (
                <img
                  src={vendorPhoto}
                  alt=""
                  className="w-9 h-9 rounded-full object-cover shrink-0 ring-2 ring-white/10"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amigo-orange to-amigo-teal flex items-center justify-center text-white font-semibold text-xs shrink-0">
                  {displayName[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-white/90 truncate">
                  {displayName}
                </p>
                <div className="flex items-center gap-1.5">
                  <span
                    className={clsx(
                      "w-2 h-2 rounded-full shrink-0",
                      isStoreOpen ? "bg-emerald-400" : "bg-red-400"
                    )}
                  />
                  <span
                    className={clsx(
                      "text-[11px] font-medium",
                      isStoreOpen ? "text-emerald-400/80" : "text-red-400/80"
                    )}
                  >
                    {isStoreOpen ? "Open" : "Closed"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Collapse Toggle ── */}
        <div
          className={clsx(
            "border-t border-white/10 shrink-0",
            collapsed ? "px-2 py-3" : "px-4 py-3"
          )}
        >
          <button
            onClick={onToggleCollapse}
            className={clsx(
              "hidden lg:flex items-center gap-2 w-full rounded-lg text-white/40 hover:text-white/70 hover:bg-sidebar-hover transition-colors",
              collapsed ? "p-2 justify-center" : "px-3 py-2 text-[12px]"
            )}
          >
            {collapsed ? (
              <PanelLeftOpen size={16} />
            ) : (
              <>
                <PanelLeftClose size={16} />
                <span>Collapse</span>
              </>
            )}
          </button>
          {!collapsed && (
            <p className="text-[10px] text-white/20 text-center mt-2">
              v1.0 — Store Dashboard
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
